import { createContext, useContext, useState, ReactNode } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea"; // Importing InputTextarea for remark field

type DialogOptions = {
    visible: boolean;
    content?: ReactNode;
    header?: string;
    onConfirm?: (remark?: string) => void;
    onCancel?: () => void;// Allow remark to be optional (string | undefined)
    remark?: string; // Remark is also optional
};

const DialogContext = createContext<{
    showExceptionDialog: (ruleId: number, ruleset: Record<number, string>) => void;
    showConfirmDialog: (header: string, message: string, onConfirm: () => void) => void;
    showConfirmRemarkDialog: (message: string, onConfirm: (remark?: string) => void) => void;
    showOptionDialog: (header: string, message: string, onConfirm: () => void, onCancel: () => void) => void;
    showReleaseDialog: (header: string, message: string, onConfirm: () => void) => void; // Updated method
    hideExceptionDialog: () => void;
    hideConfirmDialog: () => void;
    hideOptionDialog: () => void;
    hideConfirmRemarkDialog: () => void;
}>({
    showExceptionDialog: () => { },
    showConfirmDialog: () => { },
    showConfirmRemarkDialog: () => { },
    showOptionDialog: () => { },
    showReleaseDialog: () => { },
    hideExceptionDialog: () => { },
    hideConfirmDialog: () => { },
    hideConfirmRemarkDialog: () => { },
    hideOptionDialog: () => { }
});

export const DialogProvider = ({ children }: { children: ReactNode }) => {
    // Exception Dialog state
    const [exceptionDialog, setExceptionDialog] = useState<DialogOptions>({ visible: false });

    // Confirmation Dialog state
    const [confirmDialog, setConfirmDialog] = useState<DialogOptions>({ visible: false });

    // Confirmation Dialog with Remark state
    const [confirmRemarkDialog, setConfirmRemarkDialog] = useState<DialogOptions>({ visible: false, remark: "" });

    // Confirmation Dialog state
    const [optionDialog, setOptionDialog] = useState<DialogOptions>({ visible: false });

    // Show Exception Dialog
    const showExceptionDialog = (ruleId: number, ruleset: Record<number, string>) => {
        const content = ruleset[ruleId] || "Unknown rule";
        setExceptionDialog({ visible: true, content });
    };

    // Hide Exception Dialog
    const hideExceptionDialog = () => {
        setExceptionDialog({ visible: false });
    };

    // Show Confirmation Dialog
    const showConfirmDialog = (header: string, message: string, onConfirm: () => void) => {
        setConfirmDialog({ visible: true, header: header, content: message, onConfirm });
    };

    // Show Release Dialog
    const showReleaseDialog = (header: string, message: string, onConfirm: () => void) => {
        setConfirmDialog({ visible: true, header: header, content: message, onConfirm });
    };

    // Hide Confirmation Dialog
    const hideConfirmDialog = () => {
        setConfirmDialog({ visible: false });
    };

    // Show Confirmation Dialog with Remark
    const showConfirmRemarkDialog = (message: string, onConfirm: (remark?: string) => void) => {
        setConfirmRemarkDialog({ visible: true, content: message, onConfirm, remark: "" });
    };

    // Hide Confirmation Dialog with Remark
    const hideConfirmRemarkDialog = () => {
        setConfirmRemarkDialog({ visible: false, remark: "" });
    };

    // Handle change in remark input
    const handleRemarkChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setConfirmRemarkDialog(prev => ({ ...prev, remark: e.target.value }));
    };

    // Show Confirmation Dialog
    const showOptionDialog = (header: string, message: string, onConfirm: () => void, onCancel: () => void) => {
        setOptionDialog({ visible: true, header: header, content: message, onConfirm, onCancel });
    };

    // Hide Confirmation Dialog
    const hideOptionDialog = () => {
        setOptionDialog({ visible: false });
    };

    return (
        <DialogContext.Provider value={{
            showExceptionDialog,
            showConfirmDialog,
            showConfirmRemarkDialog,
            showOptionDialog,
            showReleaseDialog,
            hideExceptionDialog,
            hideConfirmDialog,
            hideConfirmRemarkDialog,
            hideOptionDialog
        }}>
            {/* Exception Dialog */}
            <Dialog
                visible={exceptionDialog.visible}
                header="Business Rule"
                footer={
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Button outlined type="button" onClick={hideExceptionDialog}>
                            OK
                        </Button>
                    </div>
                }
                onHide={hideExceptionDialog}
                closable={false}
            >
                {exceptionDialog.content}
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog
                visible={confirmDialog.visible}
                header={confirmDialog.header}
                footer={
                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                        <Button
                            outlined type="button"
                            onClick={() => {
                                confirmDialog.onCancel?.();
                                hideConfirmDialog();
                            }}>
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            severity="success"
                            onClick={() => {
                                confirmDialog.onConfirm?.();
                                hideConfirmDialog();
                            }}>
                            Confirm
                        </Button>
                    </div>
                }
                onHide={hideConfirmDialog}
                closable={false}
            >
                {confirmDialog.content}
            </Dialog>

            {/* Confirmation Dialog with Remark */}
            <Dialog
                visible={confirmRemarkDialog.visible}
                header="Confirmation"
                footer={
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                        <Button outlined type="button" onClick={hideConfirmRemarkDialog}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={() => {
                            confirmRemarkDialog.onConfirm?.(confirmRemarkDialog.remark); // Passing remark
                            hideConfirmRemarkDialog();
                        }}>
                            Confirm
                        </Button>
                    </div>
                }
                onHide={hideConfirmRemarkDialog}
                closable={false}
                style={{ width: '50vw' }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Display dialog content */}
                    {confirmRemarkDialog.content}

                    {/* The remark textarea */}
                    <InputTextarea
                        value={confirmRemarkDialog.remark}
                        onChange={handleRemarkChange}
                        rows={5}
                        cols={30}
                        placeholder="Enter your remark here"
                    />
                </div>
            </Dialog>

            {/*Option Dialog */}
            <Dialog
                visible={optionDialog.visible}
                header={optionDialog.header}
                footer={
                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                        <Button
                            outlined type="button"
                            onClick={() => {
                                optionDialog.onCancel?.();
                                hideOptionDialog();
                            }}>
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            severity="success"
                            onClick={() => {
                                optionDialog.onConfirm?.();
                                hideOptionDialog();
                            }}>
                            Confirm
                        </Button>
                    </div>
                }
                onHide={hideOptionDialog}
                closable={false}
            >
                {optionDialog.content}
            </Dialog>

            {children}
        </DialogContext.Provider>
    );
};

export const useDialog = () => useContext(DialogContext);
